import { Device, Trip, User } from "@fairfleet/geotab";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  GridColDef,
  GridOverlay,
  ToolbarPropsOverrides,
} from "@mui/x-data-grid-pro";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { areSameIds } from "../utils/areSameIds";
import { getTripOdometerKm } from "../utils/getTripOdometerKm";
import {
  CONFIRM_VEHICLE_THRESHOLD,
  countSelectedVehicles,
  exceedsVehicleCap,
  MAX_SELECTED_VEHICLES,
  needsSelectionConfirm,
} from "../utils/selectionLimit";
import { tripsLoadState } from "../utils/tripsLoadState";
import { withOdometerStarts } from "../utils/withOdometerStarts";
import { OdometerCell } from "./OdometerCell";
import { I18nGrid } from "@/components/data/I18nGrid";
import { ExportToolbar } from "@/components/grid/ExportToolbar";
import getDeviceLicensePlate from "@/hooks/getDeviceLicensePlate";
import type { TripsProgress } from "@/hooks/trips/useTripsByDateRange";
import { DateCell } from "@/pages/logbook-edit/components/grid/cells/DateCell";
import { DistanceCell } from "@/pages/logbook-edit/components/grid/cells/DistanceCell";
import { DriverEmailCell } from "@/pages/logbook-edit/components/grid/cells/DriverEmailCell";
import { DriverNameCell } from "@/pages/logbook-edit/components/grid/cells/DriverNameCell";
import { StartAddressCell } from "@/pages/logbook-edit/components/grid/cells/StartAddressCell";
import { StopAddressCell } from "@/pages/logbook-edit/components/grid/cells/StopAddressCell";
import { demystifyError } from "@/utilities/error/demystifyError";
import type { Report } from "@/utilities/export/report";
import { getUserDisplayName } from "@/utilities/user/getUserDisplayName";
import { getUserEmail } from "@/utilities/user/getUserEmail";

interface Props {
  trips?: Trip[];
  error?: Error | null;
  /** Trip searches done of all; shown while a load takes more than one batch. */
  progress?: TripsProgress;
  /** Device/driver lookups failed: the trips are shown without names, plates and drivers. */
  detailsError?: boolean;
  /** A driver filter hides other drivers' trips, so the previous trip of a vehicle is unknown. */
  driverFiltered?: boolean;
  onRetry?: () => void;
  colorMode: number;
  onColorModeChange: (mode: number) => void;
  onSelectedTripsChange?: (trips: Trip[]) => void;
  report: Omit<Report, "table">;
}

const EXPORT_FRACTION_DIGITS = { odometerStart: 1, odometerEnd: 1, distance: 1 };

/** The map colouring switch, handed to the toolbar slot (which only receives the shared toolbar props). */
const ColorModeContext = createContext<{ colorMode: number; onColorModeChange: (mode: number) => void }>({
  colorMode: 0,
  onColorModeChange: () => undefined,
});

function NoTripsOverlay() {
  const { t } = useTranslation();
  return (
    <GridOverlay>
      <Typography>{t("route.noTrips")}</Typography>
    </GridOverlay>
  );
}

// The error alert above the grid already says what went wrong; no "no trips" next to it.
function FailedOverlay() {
  return null;
}

function RouteToolbar(props: ToolbarPropsOverrides) {
  const { t } = useTranslation();
  const { colorMode, onColorModeChange } = useContext(ColorModeContext);

  return (
    // Wraps so the colour switch moves below the buttons in a narrow split pane instead of being clipped.
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ px: 0.5, flexWrap: "wrap", columnGap: 1 }}
    >
      <ExportToolbar {...props} />
      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1 }}>
        <Typography variant="body2">{t("route.gradient")}</Typography>
        <Slider
          min={0}
          max={1}
          step={1}
          marks
          value={colorMode}
          onChange={(_, value) => onColorModeChange(value as number)}
          sx={{ width: 120 }}
        />
        <Typography variant="body2">{t("route.monochrome")}</Typography>
      </Stack>
    </Stack>
  );
}

export function RouteTripsGrid({
  trips,
  error,
  progress,
  detailsError,
  driverFiltered = false,
  onRetry,
  colorMode,
  onColorModeChange,
  onSelectedTripsChange,
  report,
}: Props) {
  const { t } = useTranslation();
  const lastEmittedSelectionRef = useRef<string[]>([]);
  const colorModeValue = useMemo(() => ({ colorMode, onColorModeChange }), [colorMode, onColorModeChange]);
  const columns = useMemo<GridColDef<Trip>[]>(
    () => [
          {
            width: 220,
            field: "device.name",
            headerName: t("device"),
            valueGetter: (_: unknown, row: Trip) => (row.device as Device).name ?? "",
            filterable: false,
            sortable: false,
          },
          {
            width: 140,
            field: "device.licensePlate",
            headerName: t("licensePlate"),
            valueGetter: (_: unknown, row: Trip) => getDeviceLicensePlate(row.device as Device),
            filterable: false,
            sortable: false,
          },
          {
            width: 180,
            field: "driver.name",
            headerName: t("driver"),
            renderCell: props => <DriverNameCell props={props} />,
            valueGetter: (_: unknown, row: Trip) => {
              const driver = row.driver as User;
              return getUserDisplayName(driver);
            },
            filterable: false,
            sortable: false,
          },
          {
            width: 220,
            field: "driver.email",
            headerName: t("driverEmail"),
            renderCell: props => <DriverEmailCell props={props} />,
            valueGetter: (_: unknown, row: Trip) => getUserEmail(row.driver as User) ?? "",
            filterable: false,
            sortable: false,
          },
          {
            width: 170,
            field: "odometerStart",
            type: "number",
            headerName: t("route.odometerStart"),
            renderCell: props => <OdometerCell props={props} variant="start" />,
            valueGetter: (_: unknown, row: Trip) => getTripOdometerKm(row, "start"),
            filterable: false,
            sortable: false,
          },
          {
            width: 170,
            field: "odometerEnd",
            type: "number",
            headerName: t("route.odometerEnd"),
            renderCell: props => <OdometerCell props={props} variant="end" />,
            valueGetter: (_: unknown, row: Trip) => getTripOdometerKm(row, "end"),
            filterable: false,
            sortable: false,
          },
          {
            width: 130,
            field: "distance",
            type: "number",
            headerName: t("distance"),
            renderCell: DistanceCell,
            valueGetter: (_: unknown, row: Trip) => row.distance ?? null,
            filterable: false,
            sortable: false,
          },
          {
            width: 300,
            field: "startAddress",
            headerName: t("startAddress"),
            renderCell: StartAddressCell,
            filterable: false,
            sortable: false,
          },
          {
            width: 170,
            field: "start",
            type: "dateTime",
            headerName: t("start"),
            renderCell: DateCell,
            valueGetter: (_: unknown, row: Trip) => row.start ?? null,
            filterable: false,
            sortable: false,
          },
          {
            width: 300,
            field: "stopAddress",
            headerName: t("stopAddress"),
            renderCell: StopAddressCell,
            filterable: false,
            sortable: false,
          },
          {
            width: 170,
            field: "stop",
            type: "dateTime",
            headerName: t("stop"),
            renderCell: DateCell,
            valueGetter: (_: unknown, row: Trip) => row.stop ?? null,
            filterable: false,
            sortable: false,
          },
        ],
    [t],
  );
  const rows = useMemo(
    () => (driverFiltered ? (trips ?? []) : withOdometerStarts(trips ?? [])),
    [driverFiltered, trips],
  );
  const [selectionIds, setSelectionIds] = useState<string[]>([]);
  // A selection spanning many vehicles waits here for the user's confirmation.
  const [capExceeded, setCapExceeded] = useState(false);
  const [pendingIds, setPendingIds] = useState<string[] | null>(null);

  // Rows that disappeared with a reload cannot stay selected.
  useEffect(() => {
    const available = new Set((trips ?? []).map(trip => trip.id));
    setSelectionIds(previous => {
      const kept = previous.filter(id => available.has(id));
      return kept.length === previous.length ? previous : kept;
    });
  }, [trips]);

  const loadState = tripsLoadState(trips, error, progress);

  const applySelection = (nextIds: string[]) => {
    setSelectionIds(nextIds);
    const idSet = new Set(nextIds);
    const selectedTrips = (trips ?? []).filter(trip => idSet.has(trip.id));

    const emittedIds = selectedTrips.map(trip => trip.id);
    if (areSameIds(lastEmittedSelectionRef.current, emittedIds)) {
      return;
    }

    lastEmittedSelectionRef.current = emittedIds;
    onSelectedTripsChange?.(selectedTrips);
  };

  return (
    <ColorModeContext.Provider value={colorModeValue}>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
      }}
    >
      {loadState.kind === "capError" ? (
        <Alert severity="error">{demystifyError(t, loadState.error)}</Alert>
      ) : loadState.kind === "retryableError" ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              {t("route.retry")}
            </Button>
          }
        >
          {t("route.tripsLoadError")}
        </Alert>
      ) : null}
      {loadState.kind === "progress" ? (
        <Box sx={{ px: 1, py: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {t("route.tripsProgress", { done: loadState.progress.done, total: loadState.progress.total })}
          </Typography>
          <LinearProgress variant="determinate" value={(loadState.progress.done / loadState.progress.total) * 100} />
        </Box>
      ) : null}
      {capExceeded ? (
        <Alert severity="warning" onClose={() => setCapExceeded(false)}>
          {t("route.selectionCapExceeded", { max: MAX_SELECTED_VEHICLES })}
        </Alert>
      ) : null}
      {detailsError && !error ? <Alert severity="warning">{t("route.detailsLoadError")}</Alert> : null}
      <I18nGrid
        settingsKey="routeTrips"
        sx={{ flexGrow: 1, minHeight: 0, height: "100%" }}
        rows={rows}
        getRowId={row => row.id}
        rowSelectionModel={selectionIds}
        onRowSelectionModelChange={selection => {
          const nextIds = selection.map(id => String(id));
          if (exceedsVehicleCap(trips ?? [], selectionIds, nextIds)) {
            setCapExceeded(true);
            return;
          }
          setCapExceeded(false);
          if (needsSelectionConfirm(trips ?? [], selectionIds, nextIds)) {
            setPendingIds(nextIds);
            return;
          }
          applySelection(nextIds);
        }}
        disableColumnFilter
        disableDensitySelector
        disableRowSelectionOnClick
        checkboxSelection
        slots={{
          toolbar: RouteToolbar,
          noRowsOverlay: error ? FailedOverlay : NoTripsOverlay,
        }}
        slotProps={{
          toolbar: {
            report,
            hideFilter: true,
            hideDensity: true,
            hideQuickFilter: true,
            fractionDigits: EXPORT_FRACTION_DIGITS,
            exportDisabled: !trips || !!error,
          },
        }}
        pinnedColumns={{ left: ["__check__", "device.name"] }}
        columns={columns}
        loading={!trips && !error}
      />

      <Dialog open={pendingIds !== null} onClose={() => setPendingIds(null)}>
        <DialogTitle>{t("route.selectionConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("route.selectionConfirmBody", {
              vehicles: pendingIds ? countSelectedVehicles(trips ?? [], pendingIds) : 0,
              trips: pendingIds?.length ?? 0,
              threshold: CONFIRM_VEHICLE_THRESHOLD,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingIds(null)}>{t("route.selectionConfirmCancel")}</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (pendingIds) applySelection(pendingIds);
              setPendingIds(null);
            }}
          >
            {t("route.selectionConfirmAccept")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </ColorModeContext.Provider>
  );
}
